import React, { FC, useMemo, useRef, useState } from 'react';
import { TreeSelect } from 'antd';
import { uniq } from 'lodash';

import {
  getSplittedSearchValues,
  getTokenizedMatchedValues,
  getTokenizedUnmatchedValues,
  sortTreeDataByExactMatch,
} from '../helper';
import InputControlProps from '../InputControlProps';
import { valueToList } from './treeUtils';
import ListInputDetectionMesssage from '@/common/ListInputDetectionMessage';

export interface SelectTreeV2Props
  extends InputControlProps<string | string[]> {
  tokenizable?: boolean;
  overrideHandleChange?: (value: any) => void;
}

const SHOW_PARENT = TreeSelect.SHOW_CHILD;

const SelectTreeV2: FC<SelectTreeV2Props> = (props) => {
  const {
    tokenizable,
    overrideHandleChange,
    onChange,
    treeData,
    value,
    multiple,
  } = props;
  const [search, setSearch] = useState<string>('');

  const treeSelectComponentRef = useRef<any>(null);

  const canTokenizeInput = tokenizable && multiple;

  const handleChange = (value: string[] | string) => {
    if (typeof value === 'string') onChange(value);

    if (Array.isArray(value))
      if (overrideHandleChange) {
        overrideHandleChange(value);
      } else {
        onChange('[' + value.join(',') + ']');
      }
  };

  const handleSearch = (searchValue: string) => {
    setSearch(searchValue);
  };

  const renderDropdown = (menu: any) => {
    const isListInput =
      canTokenizeInput &&
      search.includes(',') &&
      getSplittedSearchValues(search).length > 1;

    if (isListInput) {
      const splittedSearchValues = getSplittedSearchValues(search);
      const flattenTreeDataByChildren = treeData.flatMap((node) => {
        return node.children || [];
      });
      const unmatchedValues = getTokenizedUnmatchedValues(
        splittedSearchValues,
        flattenTreeDataByChildren,
        'title',
      );
      return <ListInputDetectionMesssage unmatchedValues={unmatchedValues} />;
    }
    return menu;
  };

  const handleListSelect = (e: { keyCode: number }) => {
    if (!canTokenizeInput) {
      return;
    }

    const isListInput =
      canTokenizeInput &&
      search.includes(',') &&
      getSplittedSearchValues(search).length > 1;

    if (e.keyCode === 13 && isListInput) {
      const flattenTreeDataByChildren = treeData.flatMap((node) => {
        return node.children || [];
      });
      let splittedSearchValues = getSplittedSearchValues(search);
      let matchedValues = getTokenizedMatchedValues(
        splittedSearchValues,
        flattenTreeDataByChildren,
        'title',
      );

      const matchedOptionValues = flattenTreeDataByChildren
        .filter(({ title }) =>
          matchedValues.some(
            (item) => item.toLowerCase() === title.toString().toLowerCase(),
          ),
        )
        .map(({ value }) => value);
      const currentValues = valueToList(value);
      let newValues = [...currentValues, ...matchedOptionValues];
      newValues = uniq(newValues);

      handleChange(newValues);
      setSearch('');
      treeSelectComponentRef?.current?.blur();
    }
  };

  const getValueFromProps = () => {
    // Using this function to convert value to list
    const values = valueToList(value);

    return values;
  };
  const sortedTreeData = useMemo(
    () => sortTreeDataByExactMatch(treeData ?? [], search),
    [treeData, search],
  );

  const tProps = {
    ...props,
    treeData: sortedTreeData,
    showSearch: true,
    onChange: handleChange,
    value: getValueFromProps(),
    treeCheckable: multiple,
    multiple,
    showCheckedStrategy: SHOW_PARENT,
    searchPlaceholder: 'Please select',
    style: { width: '100%', minWidth: 200 },
    onSearch: handleSearch,
    dropdownRender: renderDropdown,
    onKeyDown: handleListSelect,
  };

  return <TreeSelect {...tProps} ref={treeSelectComponentRef} />;
};

export default SelectTreeV2;
